/**
 * Filename Utilities
 * Centralized filename generation for chart exports
 * Follows naming standards: short, descriptive, consistent
 */

export interface FilenameOptions {
  chartType: 'funding' | 'acquisition' | 'country' | 'category' | 'yoy' | 'trends';
  timeframe: string;
  context?: 'email' | 'social' | 'blog' | 'presentation';
  date?: Date;
  customSuffix?: string;
}

export class FilenameUtils {
  /**
   * Generate a standardized filename for chart exports
   */
  static generateFilename(options: FilenameOptions): string {
    const {
      chartType,
      timeframe,
      context = 'blog',
      date = new Date(),
      customSuffix
    } = options;

    // Format date as YYYY-MM-DD
    const dateStr = date.toISOString().split('T')[0];
    
    // Map chart types to short, descriptive names
    const chartTypeMap: Record<string, string> = {
      'funding': 'funding',
      'acquisition': 'acquisitions',
      'country': 'countries',
      'category': 'categories',
      'yoy': 'yoy',
      'trends': 'trends'
    };

    // Map timeframes to short, descriptive names
    const timeframeMap: Record<string, string> = {
      '1w': '1w',
      '2w': '2w',
      '4w': '4w',
      '12w': '12w',
      '6m': '6m',
      'ytd': 'ytd',
      'ttm': 'ttm',
      'custom': 'custom'
    };

    // Map contexts to short suffixes
    const contextMap: Record<string, string> = {
      'email': 'email',
      'social': 'social',
      'blog': 'blog',
      'presentation': 'pres'
    };

    // Build filename parts
    const parts = [
      'cyber', // Short prefix for cybersecurity
      chartTypeMap[chartType] || chartType,
      timeframeMap[timeframe] || timeframe,
      contextMap[context] || context
    ];

    // Add custom suffix if provided
    if (customSuffix) {
      parts.push(customSuffix);
    }

    // Add date
    parts.push(dateStr);

    // Join with hyphens and add .png extension
    return `${parts.join('-')}.png`;
  }

  /**
   * Generate filename for specific chart types with context
   * Creates descriptive, meaningful filenames based on chart content
   */
  static generateChartFilename(
    chartTitle: string,
    timeframe: string,
    context: 'email' | 'social' | 'blog' | 'presentation' = 'blog'
  ): string {
    // Extract meaningful chart type and context from title
    const title = chartTitle.toLowerCase();
    let chartType: string = 'chart';
    let chartSubtype: string = '';
    
    // Determine chart type and subtype
    if (title.includes('funding') && title.includes('distribution')) {
      chartType = 'funding';
      chartSubtype = 'distribution';
    } else if (title.includes('funding') && title.includes('trend')) {
      chartType = 'funding';
      chartSubtype = 'trends';
    } else if (title.includes('funding') && title.includes('year')) {
      chartType = 'funding';
      chartSubtype = 'yoy';
    } else if (title.includes('acquisition') || title.includes('m&a')) {
      if (title.includes('category')) {
        chartType = 'acquisitions';
        chartSubtype = 'by-category';
      } else if (title.includes('trend')) {
        chartType = 'acquisitions';
        chartSubtype = 'trends';
      } else if (title.includes('year')) {
        chartType = 'acquisitions';
        chartSubtype = 'yoy';
      } else {
        chartType = 'acquisitions';
        chartSubtype = 'activity';
      }
    } else if (title.includes('country')) {
      chartType = 'funding';
      chartSubtype = 'by-country';
    } else if (title.includes('category') && (title.includes('acquisition') || title.includes('m&a'))) {
      chartType = 'acquisitions';
      chartSubtype = 'by-category';
    } else if (title.includes('category') && title.includes('funding')) {
      chartType = 'funding';
      chartSubtype = 'by-category';
    } else if (title.includes('year') || title.includes('yoy')) {
      chartType = 'funding';
      chartSubtype = 'yoy';
    } else if (title.includes('trend')) {
      chartType = 'funding';
      chartSubtype = 'trends';
    } else {
      // Fallback: try to extract meaningful words
      if (title.includes('funding')) {
        chartType = 'funding';
        chartSubtype = 'analysis';
      } else if (title.includes('acquisition') || title.includes('m&a')) {
        chartType = 'acquisitions';
        chartSubtype = 'analysis';
      } else {
        chartType = 'cybersecurity';
        chartSubtype = 'analysis';
      }
    }

    // Format timeframe
    const timeframeMap: Record<string, string> = {
      '1w': '1w',
      '2w': '2w', 
      '4w': '4w',
      '12w': '12w',
      '6m': '6m',
      'ytd': 'ytd',
      'ttm': 'ttm',
      'custom': 'custom'
    };

    // Format context
    const contextMap: Record<string, string> = {
      'email': 'email',
      'social': 'social',
      'blog': 'blog',
      'presentation': 'pres'
    };

    // Get current date
    const dateStr = new Date().toISOString().split('T')[0];

    // Build descriptive filename (exclude context for user downloads - not needed)
    const parts = [
      chartType,
      chartSubtype,
      timeframeMap[timeframe] || timeframe,
      dateStr
    ].filter(Boolean); // Remove empty parts

    return `${parts.join('-')}.png`;
  }

  /**
   * Generate filename for country funding chart
   */
  static generateCountryFundingFilename(timeframe: string, context: 'email' | 'social' | 'blog' | 'presentation' = 'blog'): string {
    return this.generateFilename({
      chartType: 'country',
      timeframe,
      context
    });
  }

  /**
   * Generate filename for product category chart
   */
  static generateProductCategoryFilename(timeframe: string, context: 'email' | 'social' | 'blog' | 'presentation' = 'blog'): string {
    return this.generateFilename({
      chartType: 'category',
      timeframe,
      context,
      customSuffix: 'product'
    });
  }

  /**
   * Generate filename for acquisition category chart
   */
  static generateAcquisitionCategoryFilename(timeframe: string, context: 'email' | 'social' | 'blog' | 'presentation' = 'blog'): string {
    return this.generateFilename({
      chartType: 'acquisition',
      timeframe,
      context,
      customSuffix: 'category'
    });
  }

  /**
   * Generate filename for year-over-year chart
   */
  static generateYearOverYearFilename(
    timeframe: string, 
    isMA: boolean = false,
    context: 'email' | 'social' | 'blog' | 'presentation' = 'blog'
  ): string {
    return this.generateFilename({
      chartType: 'yoy',
      timeframe,
      context,
      customSuffix: isMA ? 'acquisitions' : 'funding'
    });
  }

  /**
   * Generate filename for funding trends chart
   */
  static generateFundingTrendsFilename(timeframe: string, context: 'email' | 'social' | 'blog' | 'presentation' = 'blog'): string {
    return this.generateFilename({
      chartType: 'trends',
      timeframe,
      context,
      customSuffix: 'funding'
    });
  }

  /**
   * Generate filename for M&A trends chart
   */
  static generateMATrendsFilename(timeframe: string, context: 'email' | 'social' | 'blog' | 'presentation' = 'blog'): string {
    return this.generateFilename({
      chartType: 'trends',
      timeframe,
      context,
      customSuffix: 'acquisitions'
    });
  }

  /**
   * Sanitize filename to remove invalid characters
   */
  static sanitizeFilename(filename: string): string {
    return filename
      .replace(/[^a-zA-Z0-9\-_.]/g, '-') // Replace invalid chars with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  }

  /**
   * Get file size estimate for different contexts
   */
  static getFileSizeEstimate(context: 'email' | 'social' | 'blog' | 'presentation'): string {
    const sizeMap: Record<string, string> = {
      'email': '~200KB',
      'social': '~300KB', 
      'blog': '~400KB',
      'presentation': '~500KB'
    };
    return sizeMap[context] || '~300KB';
  }
}
